import React, { useContext, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { AppContext } from '../context/AppContext.jsx';
import { Link, useLocation } from 'react-router-dom';
import * as Icon from 'react-bootstrap-icons';
import { LanguageContext, useLanguage } from "../i18n/LanguageContext.jsx";
import { getFirstImage, slugify, getSlug } from '../helpers/HelperFunctions.js';
import gsap from 'gsap';
import i18n from "../i18n/i18n";
import axios from 'axios';

const Header = () => {
    const languages = Object.keys(i18n);
    const { language } = useLanguage();
    const { switchLanguage } = useContext(LanguageContext);
    const [menu, setMenu] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [searchValue, setSearchValue] = useState(null);
    const { siteMap } = useContext(AppContext);
    const location = useLocation();
    const previousScrollYRef = useRef(0);
    const accumulatedScrollUpRef = useRef(0);
    const isHeaderHiddenRef = useRef(false);
    const previousLocationRef = useRef(location.pathname + location.search);

    // Contatore globale per la gestione dinamica degli z-index delle immagini nel menu burger
    const zIndexCounter = useRef(3);

    const pageUrl = `/${language}${process.env.REACT_APP_API_PAGE_URL}`;

    useEffect(() => {
        axios.get(`${pageUrl}/B11D5F73-8042-4798-82D9-EC526310316D`).then(res => {
            setMenu(res.data);
            setMenuItems(res.data.items.filter((el) => { return !el.notNavigable }))
            if (res.data && res.data.items && res.data.items.length > 0) {
                setCurrentImage(res.data.items[0].id);
            }
        });
    }, [pageUrl]);

    useLayoutEffect(() => {
        const onScroll = () => {
            const currentScrollY = window.scrollY;

            if (currentScrollY < 1) {
                if (!isHeaderHiddenRef.current) return;
                gsap.to('.header-wrapper', {
                    y: 0,
                    duration: 1,
                    ease: 'power2.out'
                });
                isHeaderHiddenRef.current = false;
            } else {
                const scrollDelta = currentScrollY - previousScrollYRef.current;
                previousScrollYRef.current = currentScrollY;
                const headerWrapper = document.querySelector('.header-wrapper');
                const headerContainer = document.querySelector('.header-container');

                if (!headerWrapper || !headerContainer) return;
                const headerContainerHeight = headerContainer.offsetHeight;
                const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);
                const moveUpDistance = headerWrapper.offsetHeight //headerContainerHeight + remInPixels;

                if (scrollDelta > 0) {
                    accumulatedScrollUpRef.current = 0;
                    if (!isHeaderHiddenRef.current) {
                        gsap.to(headerWrapper, {
                            y: -moveUpDistance,
                            duration: 1,
                            ease: 'power2.out'
                        });
                        isHeaderHiddenRef.current = true;
                    }
                } else if (scrollDelta < 0) {
                    accumulatedScrollUpRef.current += -scrollDelta;
                    if (accumulatedScrollUpRef.current > 10 && isHeaderHiddenRef.current) {
                        gsap.to(headerWrapper, {
                            y: 0,
                            duration: 1,
                            ease: 'power2.out'
                        });
                        isHeaderHiddenRef.current = false;
                    }
                }
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const toggleSearch = () => {
        setIsSearchOpen(!isSearchOpen);
    };

    useEffect(() => {
        const currentLocation = location.pathname + location.search;
        if (previousLocationRef.current !== currentLocation) {
            setIsMenuOpen(false);
            setIsSearchOpen(false);
            previousLocationRef.current = currentLocation;
        }
    }, [location.pathname, location.search]);

    useLayoutEffect(() => {
        if (isSearchOpen) {
            gsap.fromTo('.search-bar-container', { clipPath: 'inset(100% 0% 0%)' }, {
                clipPath: 'inset(0% 0% 0%)',
                duration: 1,
                ease: 'power3.inOut'
            });
        } else {
            gsap.fromTo('.search-bar-container', { clipPath: 'inset(0% 0% 0%)' }, {
                clipPath: 'inset(0% 0% 100%)',
                duration: 1,
                delay: 0.1,
                ease: 'power3.inOut'
            });
        }
    }, [isSearchOpen]);

    useLayoutEffect(() => {
        if (isMenuOpen) {
            gsap.fromTo('.burger-container', { clipPath: 'inset(100% 0% 0%)' }, {
                clipPath: 'inset(0% 0% 0%)',
                duration: 1,
                ease: 'power3.inOut'
            });
            gsap.fromTo('.img-current-menu-wrapper', { clipPath: 'inset(100% 0% 0%)' }, {
                clipPath: 'inset(0% 0% 0%)',
                duration: 1,
                delay: 0.1,
                ease: 'power2.inOut'
            });
        } else {
            gsap.fromTo('.img-current-menu-wrapper', { clipPath: 'inset(0% 0% 0%)' }, {
                clipPath: 'inset(0% 0% 100%)',
                duration: 1,
                ease: 'power2.inOut'
            });
            gsap.fromTo('.burger-container', { clipPath: 'inset(0% 0% 0%)' }, {
                clipPath: 'inset(0% 0% 100%)',
                duration: 1,
                delay: 0.1,
                ease: 'power3.inOut'
            });
        }
    }, [isMenuOpen]);

    return (
        <>
            <header className="header-wrapper mt-n2 mt-xl-0">
                <div className="header-container container-xl">

                    {siteMap && (
                        <nav className="navbar row justify-content-between align-items-center">
                            <div className="col-6 col-xl-2">
                                <Link to={`/${language}`} className="navbar-brand d-block w-100">
                                    <img src="/Content/img/logo-black.png" alt="logo" className="w-100" />
                                </Link>
                            </div>
                            <div className="col-6 col-xl-10">
                                <ul className="nav justify-content-end align-items-center">
                                    <li className="nav-item d-none d-xl-flex">
                                        <Link className="mx-3 text-uppercase nav-link text-uppercase p-0 position-relative font-secondary" to={`/${language}`}>Homepage</Link>
                                    </li>
                                    {menu && menuItems && menuItems.map((item, index) => (
                                        <>
                                            {item.id === "1a0fdc56-4c54-4ca5-886e-76b559428500" ?
                                                <li className="nav-item d-none d-xl-flex dropdown">
                                                    <a className="dropdown-toggle project-selector mx-3 text-white nav-link text-uppercase p-0 position-relative font-secondary"
                                                        href="#"
                                                        role="button"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false">
                                                        {item.title}
                                                    </a>
                                                    <ul className="dropdown-menu projects-menu mt-4">
                                                        <>
                                                            {item.articles?.items.map((article, i) => (
                                                                <li key={i}>
                                                                    <Link
                                                                        to={`${siteMap['1a0fdc56-4c54-4ca5-886e-76b559428500'].path}/${getSlug(article)}`}
                                                                        className={`dropdown-item text-uppercase py-2`}
                                                                    >
                                                                        <h3 className="mb-0 lh-1 fs-8">
                                                                            <span className="mb-3 small font-secondary fw-light text-uppercase">{i18n[language].project}</span><br />
                                                                            <span className="fs-6 font-secondary">{article.title}</span>
                                                                        </h3>
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </>
                                                        <li className="mt-3">
                                                            <Link
                                                                to={siteMap['1a0fdc56-4c54-4ca5-886e-76b559428500'].path}
                                                                className={`dropdown-item text-uppercase py-2`}
                                                            >
                                                                <h3 className="align-items-center d-flex fs-8 lh-1 mb-0" style={{ minHeight: "34px" }}>
                                                                    <span className="fs-6 font-secondary">{i18n[language].allProjects}</span>
                                                                </h3>
                                                            </Link>
                                                        </li>
                                                    </ul>
                                                </li>
                                                :
                                                <li key={index} className={`nav-item d-none d-xl-flex ${siteMap[item.id]?.path === window.location.pathname ? "active" : ""}`}>
                                                    <Link to={siteMap[item.id]?.path} className="mx-3 nav-link text-uppercase p-0 position-relative font-secondary">
                                                        {item.title}
                                                    </Link>
                                                </li>
                                            }
                                        </>
                                    ))}
                                    {languages && (
                                        <>
                                            <li className="dropdown ms-3" style={{ marginBottom: "-1px" }}>
                                                <a className="dropdown-toggle language-selector text-uppercase text-white font-secondary"
                                                    href="#"
                                                    role="button"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false">
                                                    {language}
                                                </a>
                                                <ul className="dropdown-menu" style={{ width: 100, minWidth: 0 }}>
                                                    <>
                                                        {languages.map((el, i) => (
                                                            <li key={i}>
                                                                <a
                                                                    href={`/${el}`}
                                                                    onClick={() => switchLanguage(el)}
                                                                    className={`dropdown-item text-uppercase px-2 ${el === language ? "active" : ""}`}
                                                                    aria-current={el === language ? "true" : "false"}
                                                                >
                                                                    <span style={{ width: "1.1rem" }} className={`fi fi-${el === "en" ? "gb" : el} me-2`}></span>
                                                                    {el}
                                                                </a>
                                                            </li>
                                                        ))}
                                                    </>
                                                </ul>
                                            </li>
                                            <li>
                                                <div className="hamburger-spacing ms-3 d-xl-none" onClick={toggleMenu}>
                                                    <div className="hamburger">
                                                        <div className="line"></div>
                                                        <div className="line"></div>
                                                        <div className="line"></div>
                                                    </div>
                                                </div>
                                            </li>
                                        </>
                                    )}
                                </ul>
                            </div>
                        </nav>
                    )}
                </div>
            </header>

            <nav className="bg-black burger-container">
                <div className="burger-menu position-relative h-100">
                    {siteMap && (
                        <div className="row h-100">
                            <div className="pointer-events-none position-absolute top-0 right-0 col-12 d-flex aling-items-center justify-content-end mt-5 pe-lg-4">
                                <div className="hamburger-spacing ms-4 open pointer-events-all" onClick={toggleMenu}>
                                    <div className="hamburger">
                                        <div className="line"></div>
                                        <div className="line"></div>
                                        <div className="line"></div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 p-5 px-4 px-md-5 px-xl-10 h-100 d-flex flex-column aling-items-center justify-content-center">
                                {menu && menuItems && (
                                    <>
                                        <div className="d-flex align-items-top justify-content-bewtween w-100 mb-5 mt-5 mt-lg-0">
                                            <div className="pe-4">
                                                <div className="burger-item mb-3">
                                                    <Link
                                                        className="burger-link fw-light fs-2"
                                                        to={`/${language}`}
                                                    >
                                                        <Icon.ArrowUpRight className="show-arrow" />
                                                        <span className="burger-title">Homepage</span>
                                                    </Link>
                                                </div>
                                                {menuItems.map((item, index) => (
                                                    <div key={index} className="burger-item mb-3">
                                                        <Link
                                                            to={siteMap[item.id]?.path}
                                                            className={`burger-link fw-light fs-2 ${siteMap[item.id]?.path === window.location.pathname ? "active" : ""}`}
                                                        >
                                                            <Icon.ArrowUpRight className="show-arrow" />
                                                            <span className="burger-title">{item.title}</span>
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            <div className="bg-white search-bar-container">
                <div className="search-menu position-relative h-100">
                    {siteMap && (
                        <>
                            <div className="row h-100">
                                <div className="d-lg-flex pt-5 pt-lg-0 px-4 flex-column justify-content-center">
                                    <div className="row row-title w-lg-100 justify-content-center">
                                        <div className="col-12 col-xl-8 d-flex align-items-center justify-content-between mb-4">
                                            <h2 className="fw-light fs-5 mb-3 position-sticky">
                                                {i18n[language].searchTitle}
                                            </h2>
                                            <div className="hamburger-spacing open pointer-events-all" onClick={toggleSearch}>
                                                <div className="hamburger">
                                                    <div className="line"></div>
                                                    <div className="line"></div>
                                                    <div className="line"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default Header;
